import '../MapboxEditor/styles/mapboxEditor.css'

import useTripApi from '@api/useTripApi'
import { ovalModeActive } from '@classes/app_url_params'
import { MapboxAwaiter } from '@components/Maps/Mapbox/mapbox_awaiter'
import type { AppState, ITrip } from '@constants/types'
import { getMapBoxApiKey } from '@constants/utils'
import type { Coordinates } from '@containers/admin/types'
import Logger from '@logger'
import { ZOOM_FACTOR } from '@store/reducers/mapConfig'
import { latLngBounds, type LatLngLiteral } from 'leaflet'
import { isBoolean, isNull } from 'lodash'
import type { LngLatLike, Map, Marker } from 'mapbox-gl'
import type { CSSProperties } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { getDataFromGpx } from '../mapOptions'
import { DAY_NIGHT_LAYER_ID } from './config/Layers'
import { createMapboxMap } from './mapbox_factory'
import {
  clearHighlightsMarkers,
  createHighlightsMarkers,
  createYachtMarker,
  drawStartEndMarkers,
  isValidLngLatLike
} from './mapbox_helpers'
import getMapboxStyle, { isApiKeyRequired } from './mapbox_style'
import { YachtOutline } from './mapbox_yacht_outline'
import MapboxPolyline from './MapboxPolyline'

const logger = Logger.create('MapboxWrapper')

export type MapboxMode = 'mapbox3d' | 'mapbox2d' | 'globe' | 'depth' | 'day_night'

interface MapboxWrapperProps {
  center?: LngLatLike
  zoom?: number
  autoFitBounds?: boolean
  interactive?: boolean
  onCreated?: (map: Map) => void
  onLoaded?: (map: Map) => void
  onZoom?: (map: Map, zoom: number) => void
  onRotate?: (map: Map) => void
  mode: MapboxMode
  style?: CSSProperties
  fullHeight?: boolean
  fullWidth?: boolean
  polylines?: (MapboxPolyline | null)[]
  className?: string
  id?: string
  scrollZoom?: boolean
  showRoute?: boolean
  showHighlights?: boolean
  showPulsatingIcon?: boolean
  showYachtOutline?: boolean
  coords?: Coordinates[]
  trip?: ITrip
}

export default function MapboxWrapper (props: MapboxWrapperProps) {
  const mapDiv = useRef<HTMLDivElement | null>(null)
  const map = useRef<Map | null>(null)
  const yachtOutline = useRef<YachtOutline | null>(null)
  const startMarkerRef = useRef<Marker | null>(null)
  const endMarkerRef = useRef<Marker | null>(null)
  const position = useSelector((state: AppState) => state.main)
  const highlightsMarkers = useRef<Marker[]>([])
  const [mapStyle, setMapStyle] = useState(getMapboxStyle(props.mode))
  const [shouldAutoCenter, setShouldAutoCenter] = useState(true)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [lineIds, setLineIds] = useState<string[]>([])
  const awaiter = useRef(new MapboxAwaiter())
  const [gpx, setGpx] = useState('')
  const { fetchCurrentTripWithGpx } = useTripApi()
  const zoom = props.zoom ? props.zoom - ZOOM_FACTOR : 5
  const [isYachtMarkerCreated, setIsYachtMarkerCreated] = useState<boolean>(false)

  const unmounted = useRef(false)

  const onMapDragStart = () => {
    if (!unmounted.current) {
      setShouldAutoCenter(false)
    }
  }

  const onMapZoom = () => {
    if (!unmounted.current && map.current && props.onZoom) {
      props.onZoom(map.current, map.current.getZoom())
    }
  }

  const onMapLoaded = () => {
    if (!unmounted.current) {
      setMapLoaded(!!map.current)
      if (map.current && props.onLoaded && !unmounted.current) {
        props.onLoaded(map.current)
      }
    }
  }

  const updatePolyLines = (polylines: (MapboxPolyline | null)[]) => {
    if (map.current && !unmounted.current) {
      const newIds = MapboxPolyline.updateMap(map.current, lineIds, polylines)
      setLineIds(newIds)
    }
  }

  const ensureMapExists = (style: string) => {
    if (!mapDiv.current) {
      logger.debug('no mapDiv')
    } else if (unmounted.current) {
      logger.debug('unmounted')
    } else if (map.current) {
      logger.debug('instance already exists')
    } else {
      const shouldCenter = !!props.center && isValidLngLatLike(props.center) && !ovalModeActive()
      const accessToken = isApiKeyRequired(style) ? getMapBoxApiKey() : undefined

      logger.verbose(`creating map for style = ${style} [token=${accessToken}]`)

      map.current = createMapboxMap({
        interactive: props.interactive !== false,
        container: mapDiv.current,
        style,
        zoom,
        center: shouldCenter ? props.center : undefined,
        pitch: props.mode === 'mapbox3d' ? 45 : 0,
        scrollZoom: isBoolean(props.scrollZoom) ? props.scrollZoom : true,
        maxZoom: 12,
        minZoom: props.mode === 'depth' ? 3 : 2,
        renderWorldCopies: true,
        attributionControl: false,
        accessToken,
        antialias: true
      })
      map.current.on('dragstart', onMapDragStart)
      map.current.on('zoom', onMapZoom)
      map.current.on('load', onMapLoaded)

      if (props.onCreated) {
        props.onCreated(map.current)
      }

      if (props.mode === 'depth') {
        void awaiter.current.addDepthLayer(map.current)
      } else if (props.mode === DAY_NIGHT_LAYER_ID) {
        void awaiter.current.addDayNightLayer(map.current)
      }
    }
  }

  useEffect(() => {
    // run on first render: if we have map container but no mapbox instance
    ensureMapExists(mapStyle)
  }, [mapDiv.current])

  // Mapbox's built-in trackResize only listens to window resize. Container-level
  // reflows (flex/grid relayout, sibling content loading, e.g. the trip widget)
  // don't fire a window resize, so the canvas stays locked to its creation-time
  // size. Observe the container and resize the map whenever it actually changes.
  useEffect(() => {
    const container = mapDiv.current
    if (!container || typeof ResizeObserver === 'undefined') {
      return
    }

    let frame = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        if (map.current && !unmounted.current) {
          map.current.resize()
        }
      })
    })

    observer.observe(container)

    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [])

  const destroyCurrentMap = () => {
    awaiter.current.cancelAll()
    if (map.current) {
      map.current.off('load', onMapLoaded)
      map.current.off('zoom', onMapZoom)
      map.current.off('dragstart', onMapDragStart)
      map.current.remove()
    }
    map.current = null
  }

  useEffect(() => {
    const newStyle = getMapboxStyle(props.mode)
    if (newStyle === mapStyle) {
      return
    }

    setMapStyle(newStyle)

    if (map.current) {
      logger.verbose(`destroying map on style changed to ${newStyle}`)
      clearHighlightsMarkers(highlightsMarkers.current, map.current)
      highlightsMarkers.current.length = 0
      removeStartEndMarkers()
      destroyCurrentMap()
      ensureMapExists(newStyle)
      setIsYachtMarkerCreated(false)
    }
  }, [props.mode])

  const updateRoutePolylines = () => {
    const { data } = getDataFromGpx(gpx)

    if (data) {
      const route: LatLngLiteral[] = data.latlngs
      const shipPosition = [position.position.value.lng, position.position.value.lat]

      if (props.polylines) {
        return updatePolyLines([
          MapboxPolyline.createItinerary(route, shipPosition),
          MapboxPolyline.route(route, shipPosition),
          ...props.polylines
        ])
      }

      return updatePolyLines([
        MapboxPolyline.createItinerary(route, shipPosition),
        MapboxPolyline.route(route, shipPosition)
      ])
    }
  }

  useEffect(() => {
    if (unmounted.current) {
      return
    }

    awaiter.current.waitForStyleLoaded(map.current).then(() => {
      if (props.showRoute && gpx) {
        updateRoutePolylines()
        addMarkersToMap()
      } else if (props.polylines) {
        updatePolyLines(props.polylines)
      }
    })
  }, [gpx, props.polylines, props.showRoute, map.current, mapLoaded])

  useEffect(() => {
    if (map.current && props.center && shouldAutoCenter) {
      map.current.flyTo({ speed: 1, center: props.center })
    }
  }, [props.center, shouldAutoCenter])

  useEffect(() => {
    if (!map.current || !isValidLngLatLike(position.position.value)) {
      return
    }

    if (props.showPulsatingIcon && !isYachtMarkerCreated) {
      createYachtMarker().setLngLat(position.position.value).addTo(map.current)
      setIsYachtMarkerCreated(true)
    }

    if (yachtOutline.current) {
      yachtOutline.current.setLatLng(position.position.value)
    }
  }, [props.showPulsatingIcon, position.position.value, map.current])

  useEffect(() => {
    if (yachtOutline.current) {
      yachtOutline.current?.setHeading(position.heading.value)
    }
  }, [position.heading.value, map.current])

  const removeYachtOutline = () => {
    if (yachtOutline.current) {
      yachtOutline.current.remove()
      yachtOutline.current = null
    }
  }

  const addMarkersToMap = () => {
    if (!map.current) {
      return
    } else if (startMarkerRef.current && endMarkerRef.current) {
      return
    }

    const { data } = getDataFromGpx(gpx)
    if (!data) {
      return
    }

    const { startMarker, endMarker } = drawStartEndMarkers(map.current, data, props?.trip)
    startMarkerRef.current = startMarker
    endMarkerRef.current = endMarker
  }

  const removeStartEndMarkers = () => {
    startMarkerRef.current?.remove()
    startMarkerRef.current = null

    endMarkerRef.current?.remove()
    endMarkerRef.current = null
  }

  useEffect(() => {
    if (!map.current || !props.showYachtOutline) {
      removeYachtOutline()
    } else if (!yachtOutline.current) {
      yachtOutline.current = new YachtOutline(map.current)
    }

    if (yachtOutline.current && isValidLngLatLike(position.position.value)) {
      yachtOutline.current.setLatLng(position.position.value)
      yachtOutline.current.setHeading(position.heading.value)
    }
  }, [props.showYachtOutline])

  // this hook is now useless since we don't have the Mercator projection in old Mapbox
  // useEffect(() => {
  //   if (!map.current) {
  //     return
  //   }
  //
  //   if (ovalModeActive()) {
  //     const extOffset = Math.floor(window.innerWidth / 25)
  //     map.current.fitBounds(
  //       [
  //         [-360, -90],
  //         [360, 90]
  //       ],
  //       { duration: 5, padding: -extOffset }
  //     )
  //
  //     // map.current.setProjection('naturalEarth')
  //   } else {
  //     // map.current.setProjection('mercator')
  //     map.current.setZoom(zoom || 1)
  //   }
  //
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [ovalModeActive(), map.current])

  useEffect(() => {
    if (props.autoFitBounds === false) {
      return
    }

    if (map.current && props.coords) {
      const bounds = latLngBounds(props.coords[0].elements)
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()

      const container = map.current?.getContainer()

      const isSmall = container && (container.offsetWidth < 200 || container.offsetHeight < 200)
      const boundsPadding = isSmall ? 10 : 75

      if (ne && sw) {
        map.current.fitBounds(
          [
            [ne.lng, ne.lat],
            [sw.lng, sw.lat]
          ],
          {
            animate: false,
            padding: {
              top: boundsPadding,
              bottom: boundsPadding,
              left: boundsPadding,
              right: boundsPadding
            }
          }
        )
      }
    } else if (map.current && gpx) {
      const { data } = getDataFromGpx(gpx)

      if (data) {
        const bounds = latLngBounds(data.latlngs)
        const ne = bounds.getNorthEast()
        const sw = bounds.getSouthWest()

        const container = map.current?.getContainer()

        const isSmall = container && (container.offsetWidth < 200 || container.offsetHeight < 200)
        const boundsPadding = isSmall ? 10 : 75

        map.current.fitBounds(
          [
            [ne.lng, ne.lat],
            [sw.lng, sw.lat]
          ],
          {
            animate: false,
            padding: {
              top: boundsPadding,
              bottom: boundsPadding,
              left: boundsPadding,
              right: boundsPadding
            }
          }
        )
      }
    }
  }, [map.current, gpx, props.coords])

  useEffect(() => {
    if (!map.current || !gpx) {
      return
    } else if (!props.showHighlights) {
      clearHighlightsMarkers(highlightsMarkers.current, map.current)
      highlightsMarkers.current = []
      return
    }

    const { waypoints } = getDataFromGpx(gpx)

    if (waypoints.length) {
      createHighlightsMarkers(waypoints, highlightsMarkers.current).forEach(marker => {
        if (!isNull(marker)) {
          if (map.current) {
            marker.addTo(map.current)
          }
          highlightsMarkers.current.push(marker)
        }
      })
    }
  }, [map.current, gpx, props.showHighlights])

  useEffect(() => {
    fetchCurrentTripWithGpx().then(({ gpx }) => {
      if (gpx && !unmounted.current) {
        setGpx(gpx)
      }
    })

    return () => {
      unmounted.current = true
      removeYachtOutline()
      removeStartEndMarkers()
      destroyCurrentMap()
    }
  }, [])

  let style = { ...(props.style || {}), minHeight: '100px', minWidth: '100px' }

  if (props.fullHeight) {
    style = { ...style, height: '100%' }
  }

  if (props.fullWidth) {
    style = { ...style, width: '100%' }
  }

  return <div ref={mapDiv} style={style} id={props.id} className={props.className} />
}
